import { motion } from "framer-motion";
import { Bug, Shield, GitMerge } from "lucide-react";
import { C, fadeUp, stagger, securityTools, categoryIcons, toolCategoryColors, toolLegend } from "./data";

export const SecurityTools = () => (
  <section className="py-20 px-6 relative" style={{ background: C.dark }} data-testid="security-tools-section">
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle, ${C.darkText4} 1px, transparent 1px)`, backgroundSize: '24px 24px' }} />
    <div className="relative z-10 max-w-7xl mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)' }}>
          <Bug className="w-4 h-4" style={{ color: '#A78BFA' }} />
          <span className="text-xs font-mono" style={{ color: '#A78BFA' }}>31 INTEGRATED SCANNERS</span>
        </motion.div>
        <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ color: C.darkText1 }}>
          Real Security Tools<span style={{ color: C.green }}>.</span> Real Scans<span style={{ color: C.green }}>.</span> Real Results<span style={{ color: C.green }}>.</span>
        </motion.h2>
        <motion.p variants={fadeUp} custom={2} className="text-base max-w-2xl mx-auto" style={{ color: C.darkText3 }}>
          Every architecture passes through production-grade security scanners. No simulations — actual tool output.
        </motion.p>
      </motion.div>

      <div className="flex flex-wrap justify-center gap-3 mb-8" data-testid="tool-legend">
        {toolLegend.map((cat) => {
          const tc = toolCategoryColors[cat.key];
          return (
            <span key={cat.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono" style={{ background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: tc.text }} />
              {cat.name}
            </span>
          );
        })}
      </div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
      >
        {securityTools.map((tool, i) => {
          const tc = toolCategoryColors[tool.category] || toolCategoryColors["IaC Scanner"];
          const CatIcon = categoryIcons[tool.category] || Shield;
          return (
            <motion.div key={i} variants={fadeUp} custom={i * 0.03}
              className="group rounded-lg p-4 transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: C.darkSurface, border: `1px solid ${C.darkBorder}` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = tc.border}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.darkBorder}
              data-testid={`tool-${tool.name.toLowerCase().replace(/[\/\s]/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: tc.bg }}>
                  <CatIcon className="w-4 h-4" style={{ color: tc.text }} />
                </div>
                <span className="text-[10px] font-bold font-mono tracking-wider px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>{tool.mono}</span>
              </div>
              <h4 className="text-sm font-semibold mb-0.5 truncate" style={{ color: C.darkText1 }}>{tool.name}</h4>
              <p className="text-[11px] mb-2 truncate" style={{ color: C.darkText4 }}>{tool.desc}</p>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.text }}>{tool.category}</span>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
        className="mt-10 rounded-xl p-8 text-center"
        style={{ background: 'rgba(0,109,67,0.1)', border: `1px solid rgba(0,109,67,0.2)` }}
      >
        <div className="flex items-center justify-center gap-3 mb-3">
          <GitMerge className="w-6 h-6" style={{ color: C.green }} />
          <h3 className="text-xl font-bold" style={{ color: C.darkText1 }}>Self-Healing IaC Generation</h3>
        </div>
        <p className="text-sm max-w-xl mx-auto" style={{ color: C.darkText3 }}>
          Violations detected by these tools are <span className="font-semibold" style={{ color: C.green }}>automatically remediated</span> before
          your IaC is exported. Zero manual intervention. Certified-clean infrastructure.
        </p>
      </motion.div>
    </div>
  </section>
);
